import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { io } from "socket.io-client";
import { useAuth } from "../../contexts/AuthContext";
import styles from "./EventDetails.module.css";

const CHAT_ENABLED = true;
const MAX_CHAT_FILES = 5;
const MAX_CHAT_FILE_SIZE = 10 * 1024 * 1024;

const ALLOWED_CHAT_FILE_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
];

const MAX_WAREHOUSE_TASK_FILES = 10;
const MAX_WAREHOUSE_TASK_FILE_SIZE = 10 * 1024 * 1024;

const ALLOWED_WAREHOUSE_TASK_FILE_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".xlsm",
];

function EventDetails() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { currentUser } = useAuth();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState("general");
  const [selectedCar, setSelectedCar] = useState(null);

  const [photosData, setPhotosData] = useState({});
  const [modalPhotoIndex, setModalPhotoIndex] = useState(null);
  const [chatModalImage, setChatModalImage] = useState(null);

  const [filesData, setFilesData] = useState({});
  const [filesLoading, setFilesLoading] = useState({});
  const [selectedDrawing, setSelectedDrawing] = useState(null);
  const [fileActionLoading, setFileActionLoading] = useState({});

  const [missingEquipment, setMissingEquipment] = useState([]);
  const [missingEquipmentLoading, setMissingEquipmentLoading] = useState(false);
  const [missingEquipmentError, setMissingEquipmentError] = useState("");

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [selectedChatFiles, setSelectedChatFiles] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatLoadedOnce, setChatLoadedOnce] = useState(false);

  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState("");
  const [documentActionLoading, setDocumentActionLoading] = useState({});

  const [warehouseTaskText, setWarehouseTaskText] = useState("");
  const [warehouseTaskFiles, setWarehouseTaskFiles] = useState([]);
  const [warehouseTaskSending, setWarehouseTaskSending] = useState(false);
  const [warehouseTaskError, setWarehouseTaskError] = useState("");
  const [warehouseTaskSuccess, setWarehouseTaskSuccess] = useState("");
  const [warehouseTaskFormOpen, setWarehouseTaskFormOpen] = useState(false);
  const [warehouseTasksRefreshing, setWarehouseTasksRefreshing] = useState(false);
const warehouseTasksRefreshingRef = useRef(false);
const lastWarehouseTasksRefreshRef = useRef("");

  const warehouseTaskFileInputRef = useRef(null);

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const chatFileInputRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const socketRef = useRef(null);
  const filesDataRef = useRef({});

  useEffect(() => {
    setIsChatOpen(false);
    setChatLoadedOnce(false);
    setMessages([]);
    setNewMessage("");
    setSelectedChatFiles([]);
    setChatModalImage(null);
    setChatError(null);
    setActiveTab("general");

    setWarehouseTaskText("");
    setWarehouseTaskFiles([]);
    setWarehouseTaskError("");
    setWarehouseTaskSuccess("");

    if (warehouseTaskFileInputRef.current) {
      warehouseTaskFileInputRef.current.value = "";
    }

    if (chatFileInputRef.current) {
      chatFileInputRef.current.value = "";
    }
  }, [eventId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpenChat = params.get("openChat") === "1";

    if (CHAT_ENABLED && shouldOpenChat) {
      setIsChatOpen(true);
    }
  }, [location.search]);

  useEffect(() => {
    try {
      setLoading(true);
      setError(null);

      const eventsData = localStorage.getItem("eventsData");
      if (!eventsData) {
        throw new Error(
          "Данные мероприятий не найдены. Вернитесь к списку мероприятий."
        );
      }

      const parsedData = JSON.parse(eventsData);

      let foundEvent = null;

      if (parsedData.past) {
        foundEvent = parsedData.past.find((e) => e.id === eventId);
      }
      if (!foundEvent && parsedData.current) {
        foundEvent = parsedData.current.find((e) => e.id === eventId);
      }
      if (!foundEvent && parsedData.future) {
        foundEvent = parsedData.future.find((e) => e.id === eventId);
      }

      if (!foundEvent) {
        throw new Error(`Мероприятие с ID ${eventId} не найдено`);
      }

      setEvent(foundEvent);
      setSelectedCar(foundEvent.cars?.[0] || null);
    } catch (err) {
      console.error("Ошибка при загрузке мероприятия:", err);
      setError(err.message || "Ошибка загрузки мероприятия");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  const rights = useMemo(() => {
    if (currentUser?.rights && typeof currentUser.rights === "object") {
      return currentUser.rights;
    }

    try {
      return JSON.parse(localStorage.getItem("userRights") || "{}");
    } catch {
      return {};
    }
  }, [currentUser]);

  const canViewDocuments = !!rights?.isManager || !!rights?.test;
  const canViewChat = true;

  const loadMissingEquipment = useCallback(async () => {
    if (!eventId) return;

    try {
      setMissingEquipmentLoading(true);
      setMissingEquipmentError("");

      const response = await fetch(`/api/missing-equipment/${eventId}`, {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      });

      if (response.status === 401) {
        navigate("/");
        return;
      }

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.message || data?.details || `Ошибка HTTP: ${response.status}`
        );
      }

      setMissingEquipment(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      console.error("Ошибка загрузки заявок:", err);
      setMissingEquipment([]);
      setMissingEquipmentError(err?.message || "Не удалось загрузить заявки");
    } finally {
      setMissingEquipmentLoading(false);
    }
  }, [eventId, navigate]);

  useEffect(() => {
    if (activeTab !== "general") return;
    loadMissingEquipment();
  }, [activeTab, loadMissingEquipment]);

  const drawings = useMemo(() => {
    if (!event?.drawings?.length) return [];

    return event.drawings.map((item, index) => {
      const name = item.name || `Чертёж ${index + 1}`;
      const lowerName = name.toLowerCase();

      return {
        id: item.id || String(index),
        name,
        isPdf: lowerName.endsWith(".pdf"),
        isDwg: lowerName.endsWith(".dwg"),
      };
    });
  }, [event]);

  useEffect(() => {
    setSelectedDrawing(drawings[0] || null);
  }, [drawings]);

  const responsibles = useMemo(() => {
    if (!event) return [];

    const items = [
      {
        key: "manager",
        label: "Менеджер",
        name: event.managerName,
        phone: event.managerTel,
      },
      {
        key: "supervisor",
        label: "Супервайзер",
        name: event.supervisorName,
        phone: event.supervisorTel,
      },
      {
        key: "engineer",
        label: "Инженер",
        name: event.engineerName,
        phone: event.engineerTel,
      },
      {
        key: "administrator",
        label: "Администратор",
        name: event.administratorName,
        phone: event.administratorTel,
      },
      {
        key: "developerEngineer",
        label: "Инженер разработки",
        name: event.developerEngineerName,
        phone: event.developerEngineerTel,
      },
      {
        key: "estimateEngineer",
        label: "Сметчик",
        name: event.estimateEngineerName,
        phone: event.estimateEngineerTel,
      },
    ];

    return items.filter((item) => item.name || item.phone);
  }, [event]);

  const equipmentFile = useMemo(() => {
    if (!selectedCar?.equipmentListId) return null;

    return {
      id: selectedCar.equipmentListId,
      name: `Оборудование — отгрузка №${selectedCar.number || ""}.pdf`,
      isPdf: true,
      isDwg: false,
    };
  }, [selectedCar]);

  const specificationFile = useMemo(() => {
    if (!event?.specification?.id) return null;

    return {
      id: event.specification.id,
      name: "Сводная информация",
      isPdf: true,
      isDwg: false,
    };
  }, [event]);

  const loadPhoto = useCallback(
    async (photoId) => {
      if (!photoId) return null;
      if (photosData[photoId]) return photosData[photoId];

      try {
        const response = await fetch(`/api/files/${photoId}`, {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "text/plain",
          },
        });

        if (response.status === 401) {
          navigate("/");
          return null;
        }

        if (!response.ok) {
          throw new Error(`Ошибка HTTP: ${response.status}`);
        }

        const base64Data = await response.text();
        const fullBase64 = `data:image/jpeg;base64,${base64Data}`;

        setPhotosData((prev) => ({ ...prev, [photoId]: fullBase64 }));
        return fullBase64;
      } catch (loadError) {
        console.error("Ошибка загрузки фото:", loadError);
        return null;
      }
    },
    [photosData, navigate]
  );

  const base64ToBlob = useCallback((base64Data, mimeType) => {
    const binaryString = window.atob(base64Data);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i += 1) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return new Blob([bytes], { type: mimeType });
  }, []);

  const loadFile = useCallback(
    async (fileItem) => {
      if (!fileItem?.id) return null;
      if (filesData[fileItem.id]) return filesData[fileItem.id];
      if (filesLoading[fileItem.id]) return null;

      try {
        setFilesLoading((prev) => ({ ...prev, [fileItem.id]: true }));

        const response = await fetch(`/api/files/${fileItem.id}`, {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "text/plain",
          },
        });

        if (response.status === 401) {
          navigate("/");
          return null;
        }

        if (!response.ok) {
          throw new Error(`Ошибка HTTP: ${response.status}`);
        }

        const base64Data = await response.text();

        let preparedData;

        if (fileItem.isPdf) {
          const pdfBlob = base64ToBlob(base64Data, "application/pdf");
          const blobUrl = window.URL.createObjectURL(pdfBlob);

          preparedData = {
            kind: "pdf",
            base64: base64Data,
            blobUrl,
          };
        } else {
          preparedData = {
            kind: "binary",
            base64: base64Data,
          };
        }

        setFilesData((prev) => {
          const oldItem = prev[fileItem.id];
          if (oldItem?.blobUrl) {
            window.URL.revokeObjectURL(oldItem.blobUrl);
          }

          return {
            ...prev,
            [fileItem.id]: preparedData,
          };
        });

        return preparedData;
      } catch (loadError) {
        console.error("Ошибка загрузки файла:", loadError);
        return null;
      } finally {
        setFilesLoading((prev) => ({ ...prev, [fileItem.id]: false }));
      }
    },
    [filesData, filesLoading, navigate, base64ToBlob]
  );

  const downloadBase64File = useCallback(
    (base64Data, fileName, mimeType) => {
      const blob = base64ToBlob(base64Data, mimeType);
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName || "file";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    },
    [base64ToBlob]
  );

  const setFileActionState = useCallback((fileId, action, value) => {
    if (!fileId) return;

    setFileActionLoading((prev) => ({
      ...prev,
      [fileId]: {
        ...prev[fileId],
        [action]: value,
      },
    }));
  }, []);

  const handleOpenFile = useCallback(
    async (fileItem) => {
      if (!fileItem?.id || !fileItem.isPdf) return;
      if (fileActionLoading[fileItem.id]?.open) return;

      try {
        setFileActionState(fileItem.id, "open", true);

        let loadedData = filesData[fileItem.id];

        if (!loadedData) {
          loadedData = await loadFile(fileItem);
        }

        if (!loadedData?.blobUrl) {
          throw new Error("Не удалось подготовить PDF для открытия");
        }

        window.open(loadedData.blobUrl, "_blank", "noopener,noreferrer");
      } catch (err) {
        console.error("Ошибка открытия файла:", err);
      } finally {
        setFileActionState(fileItem.id, "open", false);
      }
    },
    [filesData, loadFile, fileActionLoading, setFileActionState]
  );

  const handleDownloadFile = useCallback(
    async (fileItem) => {
      if (!fileItem?.id) return;
      if (fileActionLoading[fileItem.id]?.download) return;

      try {
        setFileActionState(fileItem.id, "download", true);

        let loadedData = filesData[fileItem.id];

        if (!loadedData) {
          loadedData = await loadFile(fileItem);
        }

        if (!loadedData?.base64) {
          throw new Error("Не удалось загрузить файл");
        }

        if (fileItem.isPdf) {
          downloadBase64File(
            loadedData.base64,
            fileItem.name,
            "application/pdf"
          );
        } else {
          downloadBase64File(
            loadedData.base64,
            fileItem.name,
            "application/octet-stream"
          );
        }
      } catch (err) {
        console.error("Ошибка скачивания файла:", err);
      } finally {
        setFileActionState(fileItem.id, "download", false);
      }
    },
    [
      filesData,
      loadFile,
      downloadBase64File,
      fileActionLoading,
      setFileActionState,
    ]
  );

  const loadDocuments = useCallback(() => {
    if (!canViewDocuments) return;

    try {
      setDocumentsLoading(true);
      setDocumentsError("");

      const eventDocuments = Array.isArray(event?.documents)
        ? event.documents
        : [];

      setDocuments(eventDocuments);
    } catch (err) {
      console.error("Ошибка подготовки списка документов:", err);
      setDocuments([]);
      setDocumentsError(
        err?.message || "Не удалось подготовить список документов"
      );
    } finally {
      setDocumentsLoading(false);
    }
  }, [event, canViewDocuments]);

  const handleDocumentFileAction = useCallback(
    async ({ documentId, documentName, documentType, action }) => {
      if (!documentId || !documentType || !action) return;

      const actionKey = `${documentType}:${action}:${documentId}`;

      if (documentActionLoading[actionKey]) return;

      try {
        setDocumentActionLoading((prev) => ({ ...prev, [actionKey]: true }));
        setDocumentsError("");

        const response = await fetch(
          `/api/documents/file/${documentType}/${documentId}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              Accept: "application/json",
            },
          }
        );

        if (response.status === 401) {
          navigate("/");
          return;
        }

        const data = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(
            data?.message || data?.details || `Ошибка HTTP: ${response.status}`
          );
        }

        const cleanBase64Data = normalizeBase64Data(data?.data);

        if (!cleanBase64Data) {
          throw new Error("1С не вернула данные файла");
        }

        const { mimeType, fileName } = getDocumentFileMeta({
          documentType,
          fileName: data?.name || documentName,
        });

        const fileBlob = base64ToBlob(cleanBase64Data, mimeType);
        const blobUrl = window.URL.createObjectURL(fileBlob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(blobUrl);
      } catch (err) {
        console.error(
          `Ошибка ${
            action === "download" ? "скачивания" : "открытия"
          } документа ${documentName || documentId}:`,
          err
        );
        setDocumentsError(err?.message || "Не удалось загрузить документ");
      } finally {
        setDocumentActionLoading((prev) => ({ ...prev, [actionKey]: false }));
      }
    },
    [base64ToBlob, documentActionLoading, navigate]
  );

  useEffect(() => {
    if (!selectedCar?.photos?.length || activeTab !== "cars") return;

    selectedCar.photos.forEach((photo) => {
      loadPhoto(photo.id);
    });
  }, [selectedCar, activeTab, loadPhoto]);

  useEffect(() => {
    if (
      activeTab !== "drawings" ||
      !selectedDrawing ||
      !selectedDrawing.isPdf
    ) {
      return;
    }

    loadFile(selectedDrawing);
  }, [activeTab, selectedDrawing, loadFile]);

  useEffect(() => {
    if (!equipmentFile || activeTab !== "cars") return;
    loadFile(equipmentFile);
  }, [equipmentFile, activeTab, loadFile]);

  useEffect(() => {
    if (!specificationFile || activeTab !== "general") return;
    loadFile(specificationFile);
  }, [specificationFile, activeTab, loadFile]);

  useEffect(() => {
    if (activeTab !== "documents") return;
    loadDocuments();
  }, [activeTab, loadDocuments]);

  useEffect(() => {
    filesDataRef.current = filesData;
  }, [filesData]);

  useEffect(() => {
    return () => {
      Object.values(filesDataRef.current).forEach((item) => {
        if (item?.blobUrl) {
          window.URL.revokeObjectURL(item.blobUrl);
        }
      });
    };
  }, []);

  const galleryPhotos = useMemo(() => {
    if (!selectedCar?.photos?.length) return [];

    return selectedCar.photos
      .map((photo, index) => ({
        id: photo.id,
        name: photo.name || `Фото ${index + 1}`,
        src: photosData[photo.id] || null,
      }))
      .filter((photo) => photo.src);
  }, [selectedCar, photosData]);

  const modalPhoto =
    modalPhotoIndex !== null
      ? galleryPhotos[modalPhotoIndex]?.src || null
      : null;

  const openPhotoModal = (index) => {
    if (!galleryPhotos[index]?.src) return;
    setModalPhotoIndex(index);
  };

  const closePhotoModal = useCallback(() => {
    setModalPhotoIndex(null);
  }, []);

  const showPrevPhoto = useCallback(() => {
    setModalPhotoIndex((prev) => {
      if (prev === null || galleryPhotos.length === 0) return prev;
      return prev === 0 ? galleryPhotos.length - 1 : prev - 1;
    });
  }, [galleryPhotos.length]);

  const showNextPhoto = useCallback(() => {
    setModalPhotoIndex((prev) => {
      if (prev === null || galleryPhotos.length === 0) return prev;
      return prev === galleryPhotos.length - 1 ? 0 : prev + 1;
    });
  }, [galleryPhotos.length]);

  useEffect(() => {
    closePhotoModal();
  }, [selectedCar, closePhotoModal]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (modalPhotoIndex === null) return;

      if (e.key === "Escape") closePhotoModal();
      if (e.key === "ArrowLeft") showPrevPhoto();
      if (e.key === "ArrowRight") showNextPhoto();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalPhotoIndex, closePhotoModal, showPrevPhoto, showNextPhoto]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const deltaX = touchStartX.current - touchEndX.current;

    if (Math.abs(deltaX) < 50) return;

    if (deltaX > 0) {
      showNextPhoto();
    } else {
      showPrevPhoto();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("ru-RU");
  };

  const formatMessageTime = (dateString) => {
  if (!dateString) return "";

  const rawDate = String(dateString).trim();

  const normalizedDate = rawDate.includes("T")
    ? rawDate
    : rawDate.replace(" ", "T");

  const hasTimezone =
    /z$/i.test(normalizedDate) ||
    /[+-]\d{2}:\d{2}$/.test(normalizedDate);

  const isProductionDomain =
    window.location.hostname === "dash.mf-group.com";

  /*
   * Локально время уже отображается правильно.
   * На проде PostgreSQL/Backend отдают created_at как UTC без timezone-метки,
   * поэтому только на prod-домене явно добавляем Z.
   */
  const dateSource =
    isProductionDomain && !hasTimezone
      ? `${normalizedDate}Z`
      : normalizedDate;

  const date = new Date(dateSource);

  if (Number.isNaN(date.getTime())) return "";

  return date
    .toLocaleString("ru-RU", {
      timeZone: "Europe/Moscow",
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(",", "");
};

  const formatMoney = (value) => {
    const amount = Number(value);

    if (!Number.isFinite(amount)) return "Сумма не указана";

    return `${amount.toLocaleString("ru-RU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ₽`;
  };

  const normalizeBase64Data = (value) => {
    if (!value) return "";

    return String(value)
      .replace(/^data:[^;]+;base64,/, "")
      .replace(/\s/g, "");
  };

  const getDocumentFileMeta = ({ documentType, fileName }) => {
    const isContract = documentType === "contract";

    const mimeType = isContract
      ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      : "application/vnd.ms-excel.sheet.macroEnabled.12";

    const extension = isContract ? ".docx" : ".xlsm";

    const fallbackName = isContract ? "Договор" : "Смета";
    const rawName = String(fileName || fallbackName).trim() || fallbackName;
    const nameWithoutExtension = rawName.replace(/\.[a-z0-9]+$/i, "");

    return {
      mimeType,
      fileName: `${nameWithoutExtension}${extension}`,
    };
  };

  const formatChatFileSize = (size) => {
    const bytes = Number(size || 0);

    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;

    return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
  };

  const getFileExtension = (fileName) => {
    const dotIndex = String(fileName || "").lastIndexOf(".");
    if (dotIndex === -1) return "";
    return String(fileName).slice(dotIndex).toLowerCase();
  };

  const readFileAsBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };

    reader.onerror = () => {
      reject(new Error(`Не удалось прочитать файл ${file.name}`));
    };

    reader.readAsDataURL(file);
  });
};

const openWarehouseTaskFilePicker = () => {
  warehouseTaskFileInputRef.current?.click();
};

const handleWarehouseTaskFileChange = (e) => {
  const pickedFiles = Array.from(e.target.files || []);
  if (pickedFiles.length === 0) return;

  setWarehouseTaskError("");
  setWarehouseTaskSuccess("");

  const nextFiles = [...warehouseTaskFiles];

  for (const file of pickedFiles) {
    if (nextFiles.length >= MAX_WAREHOUSE_TASK_FILES) {
      setWarehouseTaskError(
        `Можно прикрепить не больше ${MAX_WAREHOUSE_TASK_FILES} файлов`
      );
      break;
    }

    const extension = getFileExtension(file.name);

    if (!ALLOWED_WAREHOUSE_TASK_FILE_EXTENSIONS.includes(extension)) {
      setWarehouseTaskError(
        "Разрешены только файлы jpg, png, pdf, doc, docx, xls, xlsx, xlsm"
      );
      continue;
    }

    if (file.size > MAX_WAREHOUSE_TASK_FILE_SIZE) {
      setWarehouseTaskError(`Файл ${file.name} больше 10 МБ`);
      continue;
    }

    nextFiles.push(file);
  }

  setWarehouseTaskFiles(nextFiles);

  if (warehouseTaskFileInputRef.current) {
    warehouseTaskFileInputRef.current.value = "";
  }
};

const removeWarehouseTaskFile = (indexToRemove) => {
  setWarehouseTaskFiles((prev) =>
    prev.filter((_, index) => index !== indexToRemove)
  );
};

const handleSendWarehouseTask = async () => {
  const text = warehouseTaskText.trim();

  if ((!text && warehouseTaskFiles.length === 0) || warehouseTaskSending) {
    setWarehouseTaskError(
      "Введите текст задачи или прикрепите хотя бы один файл"
    );
    return;
  }

  try {
    setWarehouseTaskSending(true);
    setWarehouseTaskError("");
    setWarehouseTaskSuccess("");

    const preparedFiles = await Promise.all(
      warehouseTaskFiles.map(async (file) => ({
        name: file.name,
        mimeType: file.type || "application/octet-stream",
        size: file.size,
        data: await readFileAsBase64(file),
      }))
    );

    const response = await fetch("/api/warehouse-task", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        eventId,
        text,
        files: preparedFiles,
      }),
    });

    if (response.status === 401) {
      navigate("/");
      return;
    }

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(
        data?.message || data?.details || `Ошибка HTTP: ${response.status}`
      );
    }

    setWarehouseTaskText("");
    setWarehouseTaskFiles([]);
    setWarehouseTaskSuccess("Задача для склада отправлена");

    if (warehouseTaskFileInputRef.current) {
      warehouseTaskFileInputRef.current.value = "";
    }
  } catch (err) {
    console.error("Ошибка отправки задачи для склада:", err);
    setWarehouseTaskError(err?.message || "Не удалось отправить задачу");
  } finally {
    setWarehouseTaskSending(false);
  }
};
const findEventByIdInPayload = useCallback((source, targetEventId) => {
  if (!source) return null;

  if (Array.isArray(source)) {
    for (const item of source) {
      const found = findEventByIdInPayload(item, targetEventId);
      if (found) return found;
    }

    return null;
  }

  if (typeof source === "object") {
    if (String(source.id) === String(targetEventId)) {
      return source;
    }

    for (const value of Object.values(source)) {
      const found = findEventByIdInPayload(value, targetEventId);
      if (found) return found;
    }
  }

  return null;
}, []);

const replaceEventInCachedEvents = useCallback((source, freshEvent) => {
  if (!source || !freshEvent) return source;

  if (Array.isArray(source)) {
    let changed = false;

    const next = source.map((item) => {
      if (item && typeof item === "object" && String(item.id) === String(freshEvent.id)) {
        changed = true;
        return freshEvent;
      }

      const replaced = replaceEventInCachedEvents(item, freshEvent);

      if (replaced !== item) {
        changed = true;
      }

      return replaced;
    });

    return changed ? next : source;
  }

  if (typeof source === "object") {
    let changed = false;
    const next = { ...source };

    Object.entries(source).forEach(([key, value]) => {
      const replaced = replaceEventInCachedEvents(value, freshEvent);

      if (replaced !== value) {
        next[key] = replaced;
        changed = true;
      }
    });

    return changed ? next : source;
  }

  return source;
}, []);

const refreshWarehouseTasks = useCallback(async () => {
  if (!eventId || warehouseTasksRefreshingRef.current) return;

  try {
    warehouseTasksRefreshingRef.current = true;
    setWarehouseTasksRefreshing(true);
    setWarehouseTaskError("");

    const response = await fetch(`/api/events/${eventId}/fresh`, {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });

    if (response.status === 401) {
      navigate("/");
      return;
    }

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(
        data?.message || data?.details || `Ошибка HTTP: ${response.status}`
      );
    }

    const freshEvent = data?.event;

    if (!freshEvent) {
      throw new Error("1С не вернула мероприятие");
    }

    setEvent(freshEvent);

    setSelectedCar((prevCar) => {
      if (!freshEvent.cars?.length) return null;

      if (!prevCar?.number) {
        return freshEvent.cars[0];
      }

      return (
        freshEvent.cars.find(
          (car) => String(car.number) === String(prevCar.number)
        ) || freshEvent.cars[0]
      );
    });

    try {
      const cachedEvents = JSON.parse(
        localStorage.getItem("eventsData") || "{}"
      );

      const nextCache = { ...cachedEvents };

      ["current", "future", "past"].forEach((key) => {
        if (!Array.isArray(nextCache[key])) return;

        nextCache[key] = nextCache[key].map((item) =>
          String(item.id) === String(freshEvent.id) ? freshEvent : item
        );
      });

      localStorage.setItem("eventsData", JSON.stringify(nextCache));
      localStorage.setItem("eventsDataTimestamp", String(Date.now()));
    } catch (cacheError) {
      console.error("Ошибка обновления eventsData:", cacheError);
    }
  } catch (err) {
    console.error("Ошибка обновления задач склада:", err);
    setWarehouseTaskError(err?.message || "Не удалось обновить задачи склада");
  } finally {
    warehouseTasksRefreshingRef.current = false;
    setWarehouseTasksRefreshing(false);
  }
}, [eventId, navigate]);

useEffect(() => {
  if (activeTab !== "warehouseTask") return;

  refreshWarehouseTasks();
}, [activeTab, refreshWarehouseTasks]);

useEffect(() => {
  if (activeTab !== "warehouseTask") return;
  if (lastWarehouseTasksRefreshRef.current === eventId) return;

  lastWarehouseTasksRefreshRef.current = eventId;
  refreshWarehouseTasks();
}, [activeTab, eventId, refreshWarehouseTasks]);

  const isChatImageAttachment = (attachment) => {
    return ["image/jpeg", "image/png"].includes(attachment?.mime_type);
  };

  const getChatFileBadgeText = (attachment) => {
    const name = attachment?.original_name || "";
    const extension = getFileExtension(name).replace(".", "").toUpperCase();

    if (extension) return extension;

    if (attachment?.mime_type === "application/pdf") return "PDF";
    if (attachment?.mime_type?.includes("word")) return "DOC";
    if (
      attachment?.mime_type?.includes("excel") ||
      attachment?.mime_type?.includes("spreadsheet")
    ) {
      return "XLS";
    }

    return "FILE";
  };

  const getApiBaseUrl = () => {
    if (process.env.NODE_ENV === "development") {
      return "http://localhost:5000";
    }

    return window.location.origin;
  };

  const getChatAttachmentDownloadUrl = (attachmentId) => {
    return `${getApiBaseUrl()}/api/events/chat/attachments/${attachmentId}`;
  };

  const getChatAttachmentViewUrl = (attachmentId) => {
    return `${getApiBaseUrl()}/api/events/chat/attachments/${attachmentId}/view`;
  };

  const openChatImageModal = (attachment) => {
    if (!attachment?.id) return;

    setModalPhotoIndex(null);

    setChatModalImage({
      src: getChatAttachmentViewUrl(attachment.id),
      name: attachment.original_name || "Вложение",
    });
  };

  const closeChatImageModal = useCallback(() => {
    setChatModalImage(null);
  }, []);
  useEffect(() => {
    if (!chatModalImage) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeChatImageModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [chatModalImage, closeChatImageModal]);

  const openChatFilePicker = () => {
    chatFileInputRef.current?.click();
  };

  const handleChatFileChange = (e) => {
    const pickedFiles = Array.from(e.target.files || []);

    if (pickedFiles.length === 0) return;

    const nextFiles = [...selectedChatFiles];

    for (const file of pickedFiles) {
      if (nextFiles.length >= MAX_CHAT_FILES) {
        setChatError(`Можно прикрепить не больше ${MAX_CHAT_FILES} файлов`);
        break;
      }

      const extension = getFileExtension(file.name);

      if (!ALLOWED_CHAT_FILE_EXTENSIONS.includes(extension)) {
        setChatError(
          "Разрешены только файлы jpg, png, pdf, doc, docx, xls, xlsx"
        );
        continue;
      }

      if (file.size > MAX_CHAT_FILE_SIZE) {
        setChatError(`Файл ${file.name} больше 10 МБ`);
        continue;
      }

      nextFiles.push(file);
    }

    setSelectedChatFiles(nextFiles);

    if (chatFileInputRef.current) {
      chatFileInputRef.current.value = "";
    }
  };

  const removeSelectedChatFile = (indexToRemove) => {
    setSelectedChatFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const getStatusText = (status) => {
    switch (status) {
      case "loading":
        return "Погрузка";
      case "checking":
        return "Проверка отгрузки";
      case "sent":
        return "Уехала";
      default:
        return status || "Неизвестно";
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "loading":
        return styles.statusLoading;
      case "checking":
        return styles.statusChecking;
      case "sent":
        return styles.statusSent;
      default:
        return styles.statusDefault;
    }
  };

  const getWarehouseTaskStatusClass = (task) => {
  if (task?.executed) {
    return styles.warehouseTaskStatusDone;
  }

  if (task?.delegated) {
    return styles.warehouseTaskStatusDelegated;
  }

  return styles.warehouseTaskStatusNew;
};

  const getCurrentUserProfile = useCallback(() => {
    const normalizeProfile = (profile) => {
      if (!profile || typeof profile !== "object") return null;

      const userId =
        profile?.id ||
        profile?.userId ||
        profile?.employeeId ||
        profile?.uid ||
        profile?.guid ||
        profile?.uuid ||
        "";

      const userName =
        profile?.name ||
        profile?.fullName ||
        profile?.fio ||
        profile?.username ||
        profile?.login ||
        "";

      if (!userId || !userName) return null;

      return {
        id: String(userId),
        name: String(userName),
      };
    };

    const fromCurrentUser = normalizeProfile(currentUser);
    if (fromCurrentUser) return fromCurrentUser;

    try {
      const rawProfile = localStorage.getItem("userProfile");
      const parsedProfile = rawProfile ? JSON.parse(rawProfile) : null;
      const fromStorage = normalizeProfile(parsedProfile);

      if (fromStorage) return fromStorage;
    } catch (e) {
      console.error("Ошибка чтения userProfile из localStorage:", e);
    }

    return {
      id: "unknown-user",
      name: "Неизвестный пользователь",
    };
  }, [currentUser]);

  const isNearBottom = useCallback(() => {
    const el = chatMessagesRef.current;
    if (!el) return true;

    const threshold = 80;
    return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  }, []);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    const el = chatMessagesRef.current;
    if (!el) return;

    el.scrollTo({
      top: el.scrollHeight,
      behavior,
    });
  }, []);

  const appendMessageIfNeeded = useCallback((message) => {
    setMessages((prev) => {
      const exists = prev.some((msg) => String(msg.id) === String(message.id));
      if (exists) return prev;
      return [...prev, message];
    });
  }, []);

  const markChatAsRead = useCallback(async () => {
    if (!eventId) return;

    try {
      const currentUserProfile = getCurrentUserProfile();

      if (!currentUserProfile?.id || currentUserProfile.id === "unknown-user") {
        return;
      }

      const response = await fetch(`/api/events/${eventId}/chat/read`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          userId: currentUserProfile.id,
        }),
      });

      if (response.status === 401) {
        navigate("/");
        return;
      }

      if (!response.ok) {
        throw new Error(`Ошибка HTTP: ${response.status}`);
      }
    } catch (err) {
      console.error("Ошибка отметки чата прочитанным:", err);
    }
  }, [eventId, getCurrentUserProfile, navigate]);

  const loadMessages = useCallback(async () => {
    if (!eventId) return;

    try {
      setLoadingMessages(true);
      setChatError(null);

      const response = await fetch(`/api/events/${eventId}/chat/messages`, {
        method: "GET",
        credentials: "include",
      });

      if (response.status === 401) {
        navigate("/");
        return;
      }

      if (!response.ok) {
        throw new Error(`Ошибка HTTP: ${response.status}`);
      }

      const data = await response.json();
      const nextMessages = Array.isArray(data.messages) ? data.messages : [];
      setMessages(nextMessages);

      requestAnimationFrame(() => {
        scrollToBottom("auto");
      });
    } catch (err) {
      console.error("Ошибка загрузки сообщений:", err);
      setChatError("Не удалось загрузить сообщения");
    } finally {
      setLoadingMessages(false);
    }
  }, [eventId, navigate, scrollToBottom]);

  useEffect(() => {
    if (!CHAT_ENABLED || !isChatOpen || chatLoadedOnce || !event?.id) return;

    loadMessages().then(async () => {
      await markChatAsRead();
      setChatLoadedOnce(true);
    });
  }, [isChatOpen, chatLoadedOnce, event?.id, loadMessages, markChatAsRead]);

  useEffect(() => {
    if (!CHAT_ENABLED || !isChatOpen || !eventId) return;

    const socketUrl =
      process.env.NODE_ENV === "development"
        ? "http://localhost:5000"
        : window.location.origin;

    const socket = io(socketUrl, {
      withCredentials: true,
      transports: ["websocket", "polling"],
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("join_event_chat", { eventId });
    });

    socket.on("new_message", (message) => {
      const shouldScroll = isNearBottom();

      appendMessageIfNeeded(message);

      requestAnimationFrame(() => {
        if (shouldScroll) {
          scrollToBottom("smooth");
        }
      });

      const currentUserProfile = getCurrentUserProfile();

      if (String(message.sender_user_id) !== String(currentUserProfile.id)) {
        markChatAsRead();
      }
    });

    return () => {
      socket.emit("leave_event_chat", { eventId });
      socket.disconnect();
      socketRef.current = null;
    };
  }, [
    isChatOpen,
    eventId,
    appendMessageIfNeeded,
    isNearBottom,
    scrollToBottom,
    getCurrentUserProfile,
    markChatAsRead,
  ]);

  const handleSendMessage = async () => {
    const text = newMessage.trim();

    if (
      (!text && selectedChatFiles.length === 0) ||
      sendingMessage ||
      !eventId
    ) {
      return;
    }

    try {
      setSendingMessage(true);
      setChatError(null);

      const currentUser = getCurrentUserProfile();

      const formData = new FormData();
      formData.append("senderUserId", currentUser.id);
      formData.append("senderName", currentUser.name);
      formData.append("text", text);

      selectedChatFiles.forEach((file) => {
        formData.append("files", file);
      });

      const response = await fetch(`/api/events/${eventId}/chat/messages`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (response.status === 401) {
        navigate("/");
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message || `Ошибка отправки: ${response.status}`
        );
      }

      const createdMessage = await response.json();

      appendMessageIfNeeded(createdMessage);
      setNewMessage("");
      setSelectedChatFiles([]);

      if (chatFileInputRef.current) {
        chatFileInputRef.current.value = "";
      }

      requestAnimationFrame(() => {
        scrollToBottom("smooth");
      });

      messageInputRef.current?.focus();
    } catch (err) {
      console.error("Ошибка отправки сообщения:", err);
      setChatError(err.message || "Не удалось отправить сообщение");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleMessageKeyDown = async (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      await handleSendMessage();
    }
  };

  const sanitizePhoneForTel = (phone) => {
    if (!phone) return "";
    return String(phone).replace(/[^\d+]/g, "");
  };

  const renderPhoneLink = (phone) => {
    if (!phone) {
      return <span className={styles.emptyValue}>Не указан</span>;
    }

    return (
      <a
        href={`tel:${sanitizePhoneForTel(phone)}`}
        className={styles.phoneLink}
      >
        {phone}
      </a>
    );
  };

  if (loading) {
    return <div className={styles.loading}>Загрузка мероприятия...</div>;
  }

  if (error) {
    return <div className={styles.error}>Ошибка: {error}</div>;
  }

  if (!event) {
    return <div className={styles.noData}>Мероприятие не найдено</div>;
  }

  const selectedDrawingData = selectedDrawing
    ? filesData[selectedDrawing.id]
    : null;
  const selectedDrawingIsLoading = selectedDrawing
    ? !!filesLoading[selectedDrawing.id]
    : false;

  const carsCount = event.cars?.length || 0;

  return (
    <div className={styles.eventDetailsContainer}>
      <div className={styles.eventInfoCard}>
        <div className={styles.topRow}>
          <h3 className={styles.eventName}>
            {event.name} | {event.id}
          </h3>

          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className={styles.backButton}
          >
            Назад
          </button>
        </div>
        <div className={styles.mobileTabsSelectWrap}>
          <select
            className={styles.mobileTabsSelect}
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
          >
            <option value="general">Общая информация</option>
            <option value="responsibles">Ответственные</option>
            <option value="cars">Машины</option>
            <option value="drawings">Чертежи</option>
            {canViewDocuments && <option value="documents">Документы</option>}
            <option value="warehouseTask">Задачи для склада</option>
          </select>
        </div>

        {/* {event.debt > 0 && (
          <div className={styles.debtBadge}>
            Задолженность:{" "}
            <strong>
              {parseFloat(event.debt).toLocaleString("ru-RU", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </strong>
          </div>
        )} */}

        <div className={styles.tabs}>
          <button
            type="button"
            className={`${styles.tabButton} ${
              activeTab === "general" ? styles.tabButtonActive : ""
            }`}
            onClick={() => setActiveTab("general")}
          >
            Общая информация
          </button>

          <button
            type="button"
            className={`${styles.tabButton} ${
              activeTab === "responsibles" ? styles.tabButtonActive : ""
            }`}
            onClick={() => setActiveTab("responsibles")}
          >
            Ответственные
          </button>

          <button
            type="button"
            className={`${styles.tabButton} ${
              activeTab === "cars" ? styles.tabButtonActive : ""
            }`}
            onClick={() => setActiveTab("cars")}
          >
            Машины
          </button>

          <button
            type="button"
            className={`${styles.tabButton} ${
              activeTab === "drawings" ? styles.tabButtonActive : ""
            }`}
            onClick={() => setActiveTab("drawings")}
          >
            Чертежи
          </button>
          {canViewDocuments && (
            <button
              type="button"
              className={`${styles.tabButton} ${
                activeTab === "documents" ? styles.tabButtonActive : ""
              }`}
              onClick={() => setActiveTab("documents")}
            >
              Документы
            </button>
            
          )}
          <button
  type="button"
  className={`${styles.tabButton} ${
    activeTab === "warehouseTask" ? styles.tabButtonActive : ""
  }`}
  onClick={() => setActiveTab("warehouseTask")}
>
  Задачи для склада
</button>
        </div>

        <div className={styles.tabContent}>
          {activeTab === "general" && (
            <div className={styles.generalInfoBlock}>
              <div className={styles.generalInfoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Даты:</span>
                  <span className={styles.infoValue}>
                    {formatDate(event.dateBegin)} - {formatDate(event.dateEnd)}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Заказчик:</span>
                  <span className={styles.infoValue}>
                    {event.client || "Не указан"}
                  </span>
                </div>
              </div>

              <div className={styles.fullWidthInfoItem}>
                <span className={styles.infoLabel}>Место:</span>
                <span className={styles.infoValue}>
                  {event.location || "Не указано"}
                </span>
              </div>

              {specificationFile && (
                <div className={styles.specificationBlock}>
                  <button
                    type="button"
                    className={styles.specificationButton}
                    onClick={() => handleOpenFile(specificationFile)}
                    disabled={
                      !!filesLoading[specificationFile.id] ||
                      !!fileActionLoading[specificationFile.id]?.open ||
                      !!fileActionLoading[specificationFile.id]?.download
                    }
                  >
                    {fileActionLoading[specificationFile.id]?.open ? (
                      <>
                        <span className={styles.buttonSpinner} />
                        Открытие...
                      </>
                    ) : (
                      "Сводная информация"
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === "responsibles" && (
            <div className={styles.responsibleGrid}>
              {responsibles.length > 0 ? (
                responsibles.map((person) => (
                  <div key={person.key} className={styles.responsibleCard}>
                    <div className={styles.responsibleTitle}>
                      {person.label}
                    </div>
                    <div className={styles.responsibleName}>
                      {person.name || "Не указан"}
                    </div>
                    <div className={styles.responsiblePhone}>
                      {renderPhoneLink(person.phone)}
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.emptyBlock}>
                  Нет данных по ответственным
                </div>
              )}
            </div>
          )}

          {activeTab === "cars" && (
            <div className={styles.carsTabStack}>
              <div className={styles.carsTabHeader}>
                <h4 className={styles.sectionTitle}>Машины ({carsCount})</h4>

                {carsCount > 0 && (
                  <div className={styles.mobileCarSelectWrap}>
                    <label
                      htmlFor="carSelect"
                      className={styles.mobileCarSelectLabel}
                    >
                      Выберите машину:
                    </label>

                    <select
                      id="carSelect"
                      className={styles.mobileCarSelect}
                      value={selectedCar?.number || ""}
                      onChange={(e) => {
                        const nextCar = event.cars.find(
                          (car) => String(car.number) === e.target.value
                        );
                        if (nextCar) setSelectedCar(nextCar);
                      }}
                    >
                      {event.cars.map((car, index) => (
                        <option key={index} value={car.number}>
                          {`Отгрузка №${car.number} - ${car.car}`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {carsCount > 0 && (
                <div className={styles.carTabsRow}>
                  {event.cars.map((car, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`${styles.carTabButton} ${
                        selectedCar?.number === car.number
                          ? styles.carTabButtonActive
                          : ""
                      }`}
                      onClick={() => setSelectedCar(car)}
                    >
                      <span className={styles.carTabMain}>
                        {car.car || `Машина ${index + 1}`}
                      </span>
                      <span className={styles.carTabSub}>
                        Отгрузка №{car.number || "—"}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {carsCount === 0 ? (
                <div className={styles.emptyBlock}>Нет данных по машинам</div>
              ) : selectedCar ? (
                <>
                  <div className={styles.carDetailsPanel}>
                    <div className={styles.carInfoGrid}>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Статус:</span>
                        <span
                          className={`${styles.infoValue} ${getStatusClass(
                            selectedCar.status
                          )}`}
                        >
                          {getStatusText(selectedCar.status)}
                        </span>
                      </div>

                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Водитель:</span>
                        <span className={styles.infoValue}>
                          {selectedCar.driverName || "Не указан"}
                        </span>
                      </div>

                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Телефон:</span>
                        <span className={styles.infoValue}>
                          {renderPhoneLink(selectedCar.driverTel)}
                        </span>
                      </div>
                    </div>

                    {equipmentFile && (
                      <div className={styles.inlineEquipment}>
                        <div className={styles.inlineEquipmentHeader}>
                          <span className={styles.inlineEquipmentName}>
                            {equipmentFile.name}
                          </span>
                        </div>

                        <div className={styles.pdfActions}>
                          <button
                            type="button"
                            className={styles.pdfButton}
                            onClick={() => handleOpenFile(equipmentFile)}
                            disabled={
                              !!filesLoading[equipmentFile.id] ||
                              !!fileActionLoading[equipmentFile.id]?.open ||
                              !!fileActionLoading[equipmentFile.id]?.download
                            }
                          >
                            {fileActionLoading[equipmentFile.id]?.open ? (
                              <>
                                <span className={styles.buttonSpinner} />
                                Открытие...
                              </>
                            ) : (
                              "Открыть"
                            )}
                          </button>

                          <button
                            type="button"
                            className={styles.pdfButton}
                            onClick={() => handleDownloadFile(equipmentFile)}
                            disabled={
                              !!filesLoading[equipmentFile.id] ||
                              !!fileActionLoading[equipmentFile.id]?.open ||
                              !!fileActionLoading[equipmentFile.id]?.download
                            }
                          >
                            {fileActionLoading[equipmentFile.id]?.download ? (
                              <>
                                <span className={styles.buttonSpinner} />
                                Скачивание...
                              </>
                            ) : (
                              "Скачать"
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className={styles.carPhotos}>
                    {selectedCar.photos?.length ? (
                      <div className={styles.photosGrid}>
                        {selectedCar.photos.map((photo, index) => (
                          <div
                            key={photo.id || index}
                            className={styles.photoWrapper}
                          >
                            {photosData[photo.id] ? (
                              <img
                                src={photosData[photo.id]}
                                alt={photo.name || `Фото ${index + 1}`}
                                className={styles.carPhoto}
                                onClick={() => openPhotoModal(index)}
                              />
                            ) : (
                              <div className={styles.photoLoading}>
                                Загрузка фото...
                              </div>
                            )}

                            <span className={styles.photoName}>
                              {photo.name || `Фото ${index + 1}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.emptyBlock}>
                        Для этой машины нет фотографий
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className={styles.emptyBlock}>
                  Выберите машину, чтобы увидеть подробную информацию
                </div>
              )}
            </div>
          )}

          {activeTab === "drawings" && (
            <div className={styles.drawingsTab}>
              {drawings.length > 0 ? (
                <>
                  <div className={styles.pdfGrid}>
                    {drawings.map((drawing) => (
                      <div
                        key={drawing.id}
                        className={`${styles.pdfCard} ${
                          selectedDrawing?.id === drawing.id
                            ? styles.pdfCardActive
                            : ""
                        }`}
                        onClick={() => setSelectedDrawing(drawing)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelectedDrawing(drawing);
                          }
                        }}
                      >
                        <div className={styles.pdfInfo}>
                          <span className={styles.pdfName} title={drawing.name}>
                            {drawing.name}
                          </span>
                        </div>

                        <div className={styles.pdfActions}>
                          {drawing.isPdf && (
                            <button
                              type="button"
                              title="Открыть"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenFile(drawing);
                              }}
                              className={styles.pdfButton}
                              disabled={
                                !!filesLoading[drawing.id] ||
                                !!fileActionLoading[drawing.id]?.open ||
                                !!fileActionLoading[drawing.id]?.download
                              }
                            >
                              {fileActionLoading[drawing.id]?.open ? (
                                <>
                                  <span className={styles.buttonSpinner} />
                                  Открытие...
                                </>
                              ) : (
                                "Открыть"
                              )}
                            </button>
                          )}

                          <button
                            type="button"
                            title="Скачать"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadFile(drawing);
                            }}
                            className={styles.pdfButton}
                            disabled={
                              !!filesLoading[drawing.id] ||
                              !!fileActionLoading[drawing.id]?.open ||
                              !!fileActionLoading[drawing.id]?.download
                            }
                          >
                            {fileActionLoading[drawing.id]?.download ? (
                              <>
                                <span className={styles.buttonSpinner} />
                                Скачивание...
                              </>
                            ) : (
                              "Скачать"
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {selectedDrawing && (
                    <div className={styles.pdfPreview}>
                      <h4>Предпросмотр чертежа: {selectedDrawing.name}</h4>

                      {selectedDrawing.isPdf ? (
                        selectedDrawingIsLoading ? (
                          <div className={styles.noDrawings}>
                            Загрузка чертежа...
                          </div>
                        ) : selectedDrawingData?.blobUrl ? (
                          <iframe
                            src={selectedDrawingData.blobUrl}
                            className={styles.pdfViewer}
                            title={`Предпросмотр ${selectedDrawing.name}`}
                          />
                        ) : (
                          <div className={styles.noDrawings}>
                            Не удалось загрузить PDF для предпросмотра
                          </div>
                        )
                      ) : (
                        <div className={styles.noDrawings}>
                          Для DWG предпросмотр недоступен. Используйте кнопку
                          «Скачать».
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.noDrawings}>Нет доступных чертежей</div>
              )}
            </div>
          )}

          {activeTab === "documents" && canViewDocuments && (
            <div className={styles.documentsTab}>
              {documentsLoading ? (
                <div className={styles.emptyBlock}>Загрузка документов...</div>
              ) : documentsError ? (
                <div className={styles.emptyBlock}>{documentsError}</div>
              ) : documents.length > 0 ? (
                <div className={styles.documentsList}>
                  {documents.map((doc, index) => {
                    const documentId = doc.id;
                    const documentName = doc.name || "Документ";

                    const contractDownloadKey = `contract:download:${documentId}`;
                    const estimateDownloadKey = `estimate:download:${documentId}`;

                    const isContractDownloading =
                      !!documentActionLoading[contractDownloadKey];
                    const isEstimateDownloading =
                      !!documentActionLoading[estimateDownloadKey];

                    return (
                      <div
                        key={`${documentId || documentName}-${index}`}
                        className={styles.documentItem}
                      >
                        <div className={styles.documentHeader}>
                          <div className={styles.documentTitle}>
                            {documentName}
                          </div>
                          <div className={styles.documentSum}>
                            Сумма: {formatMoney(doc.sum)}
                          </div>
                        </div>

                        <div className={styles.documentActions}>
                          <button
                            type="button"
                            className={styles.pdfButton}
                            onClick={() =>
                              handleDocumentFileAction({
                                documentId,
                                documentName: `Договор — ${documentName}`,
                                documentType: "contract",
                                action: "download",
                              })
                            }
                            disabled={!documentId || isContractDownloading}
                          >
                            {isContractDownloading ? (
                              <>
                                <span className={styles.buttonSpinner} />
                                Скачивание...
                              </>
                            ) : (
                              "Скачать договор"
                            )}
                          </button>

                          <button
                            type="button"
                            className={styles.pdfButton}
                            onClick={() =>
                              handleDocumentFileAction({
                                documentId,
                                documentName: `Смета — ${documentName}`,
                                documentType: "estimate",
                                action: "download",
                              })
                            }
                            disabled={!documentId || isEstimateDownloading}
                          >
                            {isEstimateDownloading ? (
                              <>
                                <span className={styles.buttonSpinner} />
                                Скачивание...
                              </>
                            ) : (
                              "Скачать смету"
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={styles.emptyBlock}>Нет документов</div>
              )}
            </div>
          )}
          {activeTab === "warehouseTask" && (
  <div className={styles.warehouseTaskTab}>
    <button
      type="button"
      className={styles.warehouseTaskCollapseButton}
      onClick={() =>
        setWarehouseTaskFormOpen((prev) => !prev)
      }
    >
      {warehouseTaskFormOpen
        ? "Скрыть форму создания задачи"
        : "Новая задача для склада"}
    </button>
    

    {warehouseTaskFormOpen && (
      <div className={styles.warehouseTaskForm}>
        <label className={styles.warehouseTaskLabel}>
          <textarea
            className={styles.warehouseTaskTextarea}
            value={warehouseTaskText}
            onChange={(e) => {
              setWarehouseTaskText(e.target.value);
              setWarehouseTaskError("");
              setWarehouseTaskSuccess("");
            }}
            rows={6}
            placeholder="Опишите задачу для склада..."
            disabled={warehouseTaskSending}
          />
        </label>

        <input
          ref={warehouseTaskFileInputRef}
          type="file"
          multiple
          className={styles.warehouseTaskFileInput}
          accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.xlsm"
          onChange={handleWarehouseTaskFileChange}
        />

        {warehouseTaskFiles.length > 0 && (
          <div className={styles.warehouseTaskFiles}>
            {warehouseTaskFiles.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className={styles.warehouseTaskFile}
              >
                <span className={styles.warehouseTaskFileName}>
                  {file.name}
                </span>

                <span className={styles.warehouseTaskFileSize}>
                  {formatChatFileSize(file.size)}
                </span>

                <button
                  type="button"
                  className={styles.removeSelectedChatFile}
                  onClick={() => removeWarehouseTaskFile(index)}
                  disabled={warehouseTaskSending}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        <div className={styles.warehouseTaskActions}>
          <button
            type="button"
            className={styles.pdfButton}
            onClick={openWarehouseTaskFilePicker}
            disabled={
              warehouseTaskSending ||
              warehouseTaskFiles.length >= MAX_WAREHOUSE_TASK_FILES
            }
          >
            Прикрепить файлы
          </button>

          <button
            type="button"
            className={`${styles.pdfButton} ${styles.warehouseTaskSubmitButton}`}
            onClick={handleSendWarehouseTask}
            disabled={
              warehouseTaskSending ||
              (!warehouseTaskText.trim() &&
                warehouseTaskFiles.length === 0)
            }
          >
            {warehouseTaskSending ? (
              <>
                <span className={styles.buttonSpinner} />
                Отправка...
              </>
            ) : (
              "Отправить"
            )}
          </button>
        </div>

        {warehouseTaskError && (
          <div className={styles.warehouseTaskError}>
            {warehouseTaskError}
          </div>
        )}

        {warehouseTaskSuccess && (
          <div className={styles.warehouseTaskSuccess}>
            {warehouseTaskSuccess}
          </div>
        )}
      </div>
    )}

    <div className={styles.warehouseTasksList}>
      {Array.isArray(event.tasks) && event.tasks.length > 0 ? (
        event.tasks.map((task, index) => (
          <div
            key={task.id || index}
            className={styles.warehouseTaskCard}
          >
            <div className={styles.warehouseTaskCardHeader}>
              <div
                className={`${styles.warehouseTaskStatus} ${getWarehouseTaskStatusClass(
                  task
                )}`}
              />

              <div className={styles.warehouseTaskCardText}>
                {task.text || "Без текста"}
              </div>
            </div>

            {Array.isArray(task.files) &&
              task.files.length > 0 && (
                <div className={styles.warehouseTaskCardFiles}>
                  {task.files.map((file, fileIndex) => (
                    <button
                      key={file.id || fileIndex}
                      type="button"
                      className={styles.pdfButton}
                      onClick={() =>
                        handleDownloadFile({
                          id: file.id,
                          name: file.name,
                          isPdf: file.name
                            ?.toLowerCase()
                            .endsWith(".pdf"),
                        })
                      }
                    >
                      Скачать {file.name}
                    </button>
                  ))}
                </div>
              )}
          </div>
        ))
      ) : (
        <div className={styles.emptyBlock}>
          Нет задач для склада
        </div>
      )}
    </div>
  </div>
)}
        </div>
      </div>
      {activeTab === "general" &&
        !missingEquipmentLoading &&
        !missingEquipmentError &&
        missingEquipment.length > 0 && (
          <div className={styles.missingEquipmentCard}>
            <h3 className={styles.eventName}>Нехватки по заявкам</h3>

            <div className={styles.missingEquipmentList}>
              {missingEquipment.map((item, index) => (
                <div
                  key={`${item.name || "item"}-${index}`}
                  className={styles.missingEquipmentItem}
                >
                  <span className={styles.missingEquipmentName}>
                    {item.name || "Без названия"}
                  </span>

                  <span className={styles.missingEquipmentCount}>
                    {item.count ?? 0}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

      {CHAT_ENABLED && canViewChat && !isChatOpen && (
        <button
          type="button"
          className={styles.chatLauncher}
          onClick={() => setIsChatOpen(true)}
        >
          💬 Чат
        </button>
      )}

      {CHAT_ENABLED && canViewChat && isChatOpen && (
        <div className={styles.chatWidget}>
          <div className={styles.chatHeader}>
            <div>
              <div className={styles.chatSubtitle}>{event.name || eventId}</div>
            </div>

            <button
              type="button"
              className={styles.chatCloseButton}
              onClick={() => setIsChatOpen(false)}
              aria-label="Свернуть чат"
            >
              ✕
            </button>
          </div>

          <div className={styles.chatMessages} ref={chatMessagesRef}>
            {loadingMessages ? (
              <div className={styles.chatState}>Загрузка сообщений...</div>
            ) : messages.length === 0 ? (
              <div className={styles.chatState}>
                Напишите первое сообщение по этому мероприятию
              </div>
            ) : (
              messages.map((msg) => {
                const currentUser = getCurrentUserProfile();
                const isOwnMessage =
                  String(msg.sender_user_id) === String(currentUser.id);

                return (
                  <div
                    key={msg.id}
                    className={`${styles.chatMessageRow} ${
                      isOwnMessage ? styles.chatMessageRowOwn : ""
                    }`}
                  >
                    <div
                      className={`${styles.chatMessage} ${
                        isOwnMessage ? styles.chatMessageOwn : ""
                      }`}
                    >
                      <div className={styles.chatMessageMeta}>
                        <span className={styles.chatSender}>
                          {isOwnMessage ? "Вы" : msg.sender_name || "Участник"}
                        </span>
                        <span className={styles.chatTime}>
                          {formatMessageTime(msg.created_at)}
                        </span>
                      </div>
                      {msg.text && (
                        <div className={styles.chatText}>{msg.text}</div>
                      )}

                      {Array.isArray(msg.attachments) &&
                        msg.attachments.length > 0 && (
                          <div className={styles.chatAttachments}>
                            {msg.attachments.map((attachment) => {
                              const viewUrl = getChatAttachmentViewUrl(
                                attachment.id
                              );
                              const downloadUrl = getChatAttachmentDownloadUrl(
                                attachment.id
                              );

                              return (
                                <div
                                  key={attachment.id}
                                  className={styles.chatAttachment}
                                >
                                  {isChatImageAttachment(attachment) ? (
                                    <button
                                      type="button"
                                      className={styles.chatImageButton}
                                      onClick={() =>
                                        openChatImageModal(attachment)
                                      }
                                      title="Открыть изображение"
                                    >
                                      <img
                                        src={viewUrl}
                                        alt={
                                          attachment.original_name || "Вложение"
                                        }
                                        className={styles.chatImagePreview}
                                      />
                                    </button>
                                  ) : (
                                    <div className={styles.chatFileIcon}>
                                      {getChatFileBadgeText(attachment)}
                                    </div>
                                  )}

                                  <div className={styles.chatAttachmentInfo}>
                                    <div
                                      className={styles.chatAttachmentName}
                                      title={attachment.original_name}
                                    >
                                      {attachment.original_name || "Файл"}
                                    </div>

                                    <div className={styles.chatAttachmentMeta}>
                                      {formatChatFileSize(attachment.file_size)}
                                    </div>

                                    <div
                                      className={styles.chatAttachmentActions}
                                    >
                                      {isChatImageAttachment(attachment) ? (
                                        <button
                                          type="button"
                                          className={
                                            styles.chatAttachmentActionButton
                                          }
                                          onClick={() =>
                                            openChatImageModal(attachment)
                                          }
                                        >
                                          Открыть
                                        </button>
                                      ) : (
                                        attachment.mime_type ===
                                          "application/pdf" && (
                                          <a
                                            href={viewUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={
                                              styles.chatAttachmentAction
                                            }
                                          >
                                            Открыть
                                          </a>
                                        )
                                      )}

                                      <a
                                        href={downloadUrl}
                                        className={styles.chatAttachmentAction}
                                      >
                                        Скачать
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                    </div>
                  </div>
                );
              })
            )}

            <div ref={messagesEndRef} />
          </div>

          {chatError && <div className={styles.chatError}>{chatError}</div>}

          <div className={styles.chatInputArea}>
            <textarea
              ref={messageInputRef}
              className={styles.chatTextarea}
              placeholder="Введите сообщение..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleMessageKeyDown}
              disabled={sendingMessage}
            />

            {selectedChatFiles.length > 0 && (
              <div className={styles.selectedChatFiles}>
                {selectedChatFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className={styles.selectedChatFile}
                  >
                    <span className={styles.selectedChatFileName}>
                      {file.name}
                    </span>

                    <span className={styles.selectedChatFileSize}>
                      {formatChatFileSize(file.size)}
                    </span>

                    <button
                      type="button"
                      className={styles.removeSelectedChatFile}
                      onClick={() => removeSelectedChatFile(index)}
                      disabled={sendingMessage}
                      aria-label="Убрать файл"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            <input
              ref={chatFileInputRef}
              type="file"
              multiple
              className={styles.chatFileInput}
              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx"
              onChange={handleChatFileChange}
            />

            <div className={styles.chatInputActions}>
              <button
                type="button"
                className={styles.chatAttachButton}
                onClick={openChatFilePicker}
                disabled={
                  sendingMessage || selectedChatFiles.length >= MAX_CHAT_FILES
                }
                title="Прикрепить файл"
              >
                <img
                  src="/clip.png"
                  alt="Прикрепить файл"
                  className={styles.chatAttachButtonIcon}
                />
              </button>

              <button
                type="button"
                className={styles.chatSendButton}
                onClick={handleSendMessage}
                disabled={
                  sendingMessage ||
                  (!newMessage.trim() && selectedChatFiles.length === 0)
                }
              >
                {sendingMessage ? "Отправка..." : "Отправить"}
              </button>
            </div>
          </div>
        </div>
      )}
      {chatModalImage && (
        <div className={styles.photoModalOverlay} onClick={closeChatImageModal}>
          <div
            className={styles.photoModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={styles.chatImageModalClose}
              onClick={closeChatImageModal}
              aria-label="Закрыть изображение"
            >
              ✕
            </button>

            <img
              src={chatModalImage.src}
              alt={chatModalImage.name || "Вложение"}
              className={styles.modalPhoto}
            />
          </div>
        </div>
      )}

      {modalPhoto && (
        <div className={styles.photoModalOverlay} onClick={closePhotoModal}>
          <div
            className={styles.photoModalContent}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {galleryPhotos.length > 1 && (
              <>
                <button
                  type="button"
                  className={`${styles.photoNavButton} ${styles.photoNavPrev}`}
                  onClick={showPrevPhoto}
                  aria-label="Предыдущее фото"
                >
                  ‹
                </button>

                <button
                  type="button"
                  className={`${styles.photoNavButton} ${styles.photoNavNext}`}
                  onClick={showNextPhoto}
                  aria-label="Следующее фото"
                >
                  ›
                </button>
              </>
            )}

            <img
              src={modalPhoto}
              alt={galleryPhotos[modalPhotoIndex]?.name || "Увеличенное фото"}
              className={styles.modalPhoto}
            />

            {galleryPhotos.length > 1 && (
              <div className={styles.photoCounter}>
                {modalPhotoIndex + 1} / {galleryPhotos.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default EventDetails;
